import React, { Dispatch, SetStateAction, useRef } from 'react';
import SelectorOption from './SelectorOption';
import { SalvSearchField, Size } from '@salvapp/ui-components';
import { SelectorOptionMap, SelectorOptionType } from './Selector';
import { ManualCustomListSelectorCode, selectorConfigByType } from './ListGroups';
import { useFilter } from '@react-aria/i18n';
import { useVirtualizer } from '@tanstack/react-virtual';

interface Props {
  index: number;
  type: ManualCustomListSelectorCode;
  selectorOptionMap: SelectorOptionMap;
  setSelectorOptionMap: Dispatch<SetStateAction<SelectorOptionMap>>;
  navigateFromOption: (option: SelectorOptionType, optionLevel: number) => void;
  cancelChildrenNavigation: (optionLevel: number) => void;
  navigationIndex: Record<number, string>;
  filters: string[];
  setFilters: Dispatch<SetStateAction<string[]>>;
  isGroupEdited: boolean;
}

const SelectorOptionColumn = ({
  index,
  type,
  selectorOptionMap,
  setSelectorOptionMap,
  navigateFromOption,
  cancelChildrenNavigation,
  navigationIndex,
  filters,
  setFilters,
  isGroupEdited,
}: Props) => {
  const { contains } = useFilter({ sensitivity: 'base' });
  const selectorConfig = selectorConfigByType[type];
  const { rootOptionValue } = selectorConfig;
  const optionDepth = selectorConfig.selectorOptionHeaders.length;

  const optionLevel = index + 1;

  const filter = filters[optionLevel];
  const setFilter = (value: string) => {
    if (value) {
      cancelChildrenNavigation(optionLevel);
    } else {
      // Restore navigation
      if (levelOptions?.[0]) {
        navigateFromOption(levelOptions[0], optionLevel);
      }
    }
    setFilters((prev) => {
      prev[optionLevel] = value;
      return [...prev];
    });
  };

  const previousOptionLevel = optionLevel - 1;
  const levelOptions = (
    optionLevel === 1
      ? selectorOptionMap.get(rootOptionValue)!.children
      : selectorOptionMap.get(navigationIndex[previousOptionLevel])?.children
  )?.filter((levelOption) => contains(levelOption.title, filter));

  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: levelOptions?.length ?? 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 72, // usual height of a program selector option
    overscan: 5, //how many items to render above and below the visible area
  });

  return (
    <div key={optionLevel} className={'selector-option-column'}>
      <div className={'selector-option-filter'}>
        <SalvSearchField
          label={selectorConfig.selectorOptionHeaders[index]}
          size={Size.SMALL}
          placeholder={'All'}
          value={filter}
          onChange={setFilter}
          onSubmit={setFilter}
        />
      </div>
      <div className={'selector-option-list'} ref={parentRef}>
        <div
          className={'virtualizer-wrapper'}
          style={{
            height: `${virtualizer.getTotalSize()}px`,
          }}
        >
          <div
            style={{
              transform: `translateY(${virtualizer.getVirtualItems()[0]?.start ?? 0}px)`,
            }}
          >
            {virtualizer.getVirtualItems().map((virtualItem) => {
              const option = levelOptions![virtualItem.index];
              return (
                <SelectorOption
                  key={option.value}
                  index={virtualItem.index}
                  option={option}
                  setOptions={setSelectorOptionMap}
                  navigate={() => navigateFromOption(option, optionLevel)}
                  navigated={navigationIndex[optionLevel] === option.value}
                  level={optionLevel}
                  optionDepth={optionDepth}
                  isEdit={isGroupEdited}
                  measureElement={virtualizer.measureElement}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectorOptionColumn;
